import { useQuery } from '@tanstack/react-query';

import { appConfigStore } from 'Models/AppConfigStore';
import { Journey } from 'Global/Journey/Types/Journey';
import { FetchTripClassPrices, TripClassPrice } from 'Services/Api/_HumanWritten/PricingService/PricingService';

/**
 * Whether the fare classes are shown on the ticket cards.
 *
 * <p><b>On.</b> It was off while the strip under each card failed to earn its place and while every
 * class priced at $0 — the fare resolved across all eight passenger slots including the unconfigured
 * ones, which are free by default. Both are settled: the cards now draw a priced column per class,
 * and the fares resolve across the site's active passenger types only.</p>
 *
 * <p>Still a switch rather than nothing, because a vessel with classes is a configuration choice and
 * this is the one place to take the whole feature off a card if it goes wrong in front of
 * customers.</p>
 */
export const TRIP_CLASS_PRICES_ENABLED = true;

export interface TripClassPrices {
	prices: { [tripId: string]: TripClassPrice[] };
	/**
	 * Whether the answer is still on its way.
	 *
	 * <p>Returned, not swallowed. Without it "no classes yet" and "no classes" are the same value, so
	 * the ticket list drew the plain card, the fares arrived, and it redrew as a fare table — a flicker
	 * on every window, and the wrong layout for however long the request took.</p>
	 */
	isLoading: boolean;
}

/**
 * Class fares for a window of sailings, keyed by trip id.
 *
 * <p>One request for the whole window rather than one per card. The key is built from the journeys
 * so scrolling to a new day fetches once and going back is a cache hit.</p>
 */
export function useTripClassPrices(journeys: Journey[]): TripClassPrices {
	const key = journeys
		.map(journey => `${journey.tripId}|${journey.startStopId}|${journey.endStopId}`)
		.sort()
		.join(',');

	const enabled =
		TRIP_CLASS_PRICES_ENABLED && journeys.length > 0 && appConfigStore.passengerClassesEnabled;

	const { data, isPending } = useQuery({
		queryKey: ['trip-class-prices', key],
		queryFn: () => FetchTripClassPrices(journeys),
		enabled,
		staleTime: 60_000,
	});

	// A DISABLED query is "pending" forever, which is not the same as waiting on one — hence the
	// `enabled &&`. The layout no longer turns on this (the setting decides that), so this only holds
	// the fares back until they arrive rather than deciding which table to draw.
	return { prices: data ?? {}, isLoading: enabled && isPending };
}
